import React, { useState } from "react"
import {
  Button,
  Badge,
  Alert,
  Spinner,
  ProgressBar,
  Table,
  Modal,
  Card,
  Input,
  Textarea,
  Select,
  Label,
  InputGroup,
  Pagination,
  PageHeader,
} from "./ui"

// /styleguide — visual gallery of the Tailwind UI kit (ticket 25824). Lets the
// reviewer sign off each component/state in the real asset pipeline.

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="mb-4 border-b border-gray-200 pb-1 text-lg font-semibold text-integria-green">
        {title}
      </h2>
      <div className="flex flex-wrap items-start gap-4">{children}</div>
    </section>
  )
}

export default function Styleguide() {
  const [showModal, setShowModal] = useState(false)
  const [page, setPage] = useState(1)

  return (
    <div className="mx-auto max-w-5xl px-6 py-10 font-inter">
      <header className="mb-10">
        <h1 className="text-2xl font-bold text-integria-green">Tailwind UI Kit</h1>
        <p className="mt-1 text-sm text-gray-600">
          Bootstrap replacement components (ticket 25824). Review each state below.
        </p>
      </header>

      <Section title="Buttons">
        <Button variant="success">Success</Button>
        <Button variant="primary">Primary</Button>
        <Button variant="secondary">Secondary</Button>
        <Button variant="danger">Danger</Button>
        <Button variant="link">Link</Button>
        <Button variant="primary" disabled>
          Disabled
        </Button>
      </Section>

      <Section title="Badges">
        <Badge bg="primary">Primary</Badge>
        <Badge bg="secondary">Secondary</Badge>
        <Badge bg="success">Success</Badge>
        <Badge bg="danger">Danger</Badge>
        <Badge bg="warning">Warning</Badge>
        <Badge bg="info">Info</Badge>
      </Section>

      <Section title="Alerts">
        <div className="w-full space-y-2">
          <Alert variant="primary">A primary alert.</Alert>
          <Alert variant="success">A success alert.</Alert>
          <Alert variant="danger">A danger alert.</Alert>
          <Alert variant="warning">A warning alert.</Alert>
          <Alert variant="info">An info alert.</Alert>
        </div>
      </Section>

      <Section title="Spinner & ProgressBar">
        <Spinner size="sm" />
        <Spinner size="md" />
        <Spinner size="lg" />
        <div className="w-64">
          <ProgressBar now={40} />
        </div>
        <div className="w-64">
          <ProgressBar now={75} label="75%" />
        </div>
      </Section>

      <Section title="Cards">
        <Card className="w-64">
          <Card.Img src="https://placehold.co/600x400" alt="placeholder" />
          <Card.Body>
            <Card.Title>Product name</Card.Title>
            <Card.Text>A short product description goes here.</Card.Text>
            <Button variant="primary" className="mt-3">
              Add
            </Button>
          </Card.Body>
        </Card>
      </Section>

      <Section title="Form controls">
        <div className="w-full max-w-md space-y-4">
          <div>
            <Label htmlFor="sg-name">Name</Label>
            <Input id="sg-name" placeholder="Jane Doe" defaultValue="" />
          </div>
          <div>
            <Label htmlFor="sg-company">Company</Label>
            <Select id="sg-company" defaultValue="AU">
              <option value="AU">Australia</option>
              <option value="NZ">New Zealand</option>
            </Select>
          </div>
          <div>
            <Label htmlFor="sg-markup">Markup</Label>
            <InputGroup>
              <InputGroup.Text>$</InputGroup.Text>
              <Input id="sg-markup" className="rounded-l-none" defaultValue="20" />
            </InputGroup>
          </div>
          <div>
            <Label htmlFor="sg-notes">Notes</Label>
            <Textarea id="sg-notes" placeholder="Notes…" />
          </div>
          <div>
            <Label htmlFor="sg-invalid">Invalid field</Label>
            <Input id="sg-invalid" isInvalid defaultValue="bad value" />
          </div>
        </div>
      </Section>

      <Section title="Table">
        <Table className="w-full">
          <thead>
            <tr>
              <th>Product</th>
              <th>Qty</th>
              <th>Price</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Vitamin C</td>
              <td>2</td>
              <td>$24.00</td>
            </tr>
            <tr>
              <td>Magnesium</td>
              <td>1</td>
              <td>$18.50</td>
            </tr>
          </tbody>
        </Table>
      </Section>

      <Section title="PageHeader">
        <div className="w-full space-y-6">
          <PageHeader
            title="Patients"
            action={<Button variant="primary">Create new prescription</Button>}
          />
          <PageHeader
            title="Jane Doe"
            subtitle="jane@example.com"
            action={
              <Button variant="secondary" className="px-3 py-1.5 text-xs">
                Edit Details
              </Button>
            }
          />
          <PageHeader title="Transactions" />
        </div>
      </Section>

      <Section title="Pagination">
        <div className="w-full">
          <Pagination
            page={page}
            totalPages={5}
            totalCount={92}
            onPageChange={setPage}
          />
        </div>
      </Section>

      <Section title="Modal">
        <Button variant="primary" onClick={() => setShowModal(true)}>
          Open modal
        </Button>
        <Modal show={showModal} onHide={() => setShowModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Example modal</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-sm text-gray-600">
              This modal is built on Headless UI with Tailwind styling.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => setShowModal(false)}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      </Section>
    </div>
  )
}
